import { GenericToolbar } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Label } from '@/components/ui/shadcn/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import IndexLayout from '@/layouts/index-layout';
import { cn, formatDate as libFormatDate } from '@/lib/utils';
import { type PageProps, type PaginationMeta } from '@/types';
import type { PurchaseOrderListItem, PurchaseOrderStatus } from '@/types/purchase-order';
import { Link, router, usePage } from '@inertiajs/react';
import { AlertCircle, ClipboardList, Download, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { route } from 'ziggy-js';

interface SupplierOption {
    uuid: string;
    name: string;
}

interface ProjectOption {
    uuid: string;
    contract_name: string;
    contract_number: string;
}

interface LeadOption {
    uuid: string;
    lead_number: string | null;
}

interface CreatorOption {
    uuid: string;
    name: string;
}

interface PurchaseOrdersPageProps extends PageProps {
    [key: string]: unknown;
    purchaseOrders: {
        data: PurchaseOrderListItem[];
        meta?: PaginationMeta;
        current_page?: number;
        last_page?: number;
        per_page?: number;
        total?: number;
        from?: number | null;
        to?: number | null;
    };
    suppliers: SupplierOption[];
    projects: ProjectOption[];
    leads: LeadOption[];
    creators: CreatorOption[];
    filters: {
        search?: string;
        status?: string;
        supplier_id?: string;
        project_id?: string;
        lead_id?: string;
        created_by?: string;
        date_from?: string;
        date_to?: string;
        overdue?: boolean;
        per_page?: number;
        page?: number;
    };
}

const STATUS_COLORS: Record<PurchaseOrderStatus, string> = {
    draft: 'border-border bg-muted text-muted-foreground',
    ordered: 'border-info-subtle bg-info-subtle text-info-subtle-foreground',
    partially_received: 'border-warning-subtle bg-warning-subtle text-warning-subtle-foreground',
    received: 'border-success-subtle bg-success-subtle text-success-subtle-foreground',
    cancelled: 'border-danger-subtle bg-danger-subtle text-danger-subtle-foreground',
};

const STATUS_OPTIONS: PurchaseOrderStatus[] = ['draft', 'ordered', 'partially_received', 'received', 'cancelled'];

const DEFAULT_COLUMNS = [
    'po_number',
    'supplier',
    'project_lead',
    'status',
    'order_date',
    'expected_delivery',
    'directo_number',
    'total',
    'created_by',
];

type PurchaseOrderColumn = (typeof DEFAULT_COLUMNS)[number];
type SortDirection = 'asc' | 'desc';

function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
    const { t } = useTranslations();
    const label = t(`purchase_orders.statuses.${status}`, status.replace(/_/g, ' '));

    return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[status]}`}>{label}</span>;
}

function formatCurrency(amount: number | string | null | undefined, currency: string): string {
    const num = Number(amount ?? 0);

    return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: currency || 'EUR' }).format(num);
}

function formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    return libFormatDate(date);
}

function creatorName(po: PurchaseOrderListItem): string {
    if (!po.creator) {
        return '—';
    }

    return `${po.creator.first_name} ${po.creator.last_name}`.trim() || '—';
}

function projectLeadLabel(po: PurchaseOrderListItem): string {
    // A lead that has become a project is shown by its project, with the lead as context.
    const convertedProject = po.project?.lead_id === po.lead?.id ? po.project : po.lead?.project;

    if (po.lead && convertedProject?.lead_id === po.lead.id) {
        return `${convertedProject.contract_name} ${po.lead.lead_number ?? ''}`.trim();
    }

    if (po.project) {
        return po.project.contract_name;
    }

    if (po.lead) {
        return po.lead.lead_number ?? '';
    }

    return 'Warehouse / Stock';
}

function purchaseOrderColumnValue(po: PurchaseOrderListItem, column: string): string | number | Date | null {
    switch (column) {
        case 'po_number':
            return po.po_number;
        case 'supplier':
            return po.supplier?.name ?? '';
        case 'project_lead':
            return projectLeadLabel(po);
        case 'status':
            return po.status;
        case 'order_date':
            return po.order_date ? new Date(po.order_date) : null;
        case 'expected_delivery':
            return po.expected_delivery_date ? new Date(po.expected_delivery_date) : null;
        case 'total':
            return Number(po.subtotal ?? 0);
        case 'directo_number':
            return po.directo_number ?? '';
        case 'created_by':
            return creatorName(po);
        default:
            return '';
    }
}

export default function PurchaseOrdersIndex() {
    const { props } = usePage<PurchaseOrdersPageProps>();
    const { purchaseOrders, suppliers, projects, leads, creators, filters = {} } = props;
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [searchValue, setSearchValue] = useState(filters.search ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(DEFAULT_COLUMNS));
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(() => ({
        status: filters.status ? [filters.status] : [],
        supplier: filters.supplier_id ? [filters.supplier_id] : [],
        project_lead: filters.project_id ? [filters.project_id] : filters.lead_id ? [filters.lead_id] : [],
        created_by: filters.created_by ? [filters.created_by] : [],
    }));
    const [orderDateFrom, setOrderDateFrom] = useState<string>(filters.date_from ?? '');
    const [orderDateTo, setOrderDateTo] = useState<string>(filters.date_to ?? '');
    const [overdueOnly, setOverdueOnly] = useState<boolean>(Boolean(filters.overdue));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);

    const { applyFilters, loading } = useServerFilters(filters, '/purchase-orders');

    const breadcrumbs = [
        { title: t('dashboard.title', 'Dashboard'), href: '/' },
        { title: t('purchase_orders.index.title', 'Purchase Orders'), href: '#' },
    ];

    const columnDefinitions = useMemo(
        () => [
            { id: 'po_number', label: t('purchase_orders.index.columns.po_number', 'PO Number') },
            { id: 'supplier', label: t('purchase_orders.index.columns.supplier', 'Supplier') },
            { id: 'project_lead', label: t('purchase_orders.index.columns.project_lead', 'Project / Lead') },
            { id: 'status', label: t('purchase_orders.index.columns.status', 'Status') },
            { id: 'order_date', label: t('purchase_orders.index.columns.order_date', 'Order Date') },
            { id: 'expected_delivery', label: t('purchase_orders.index.columns.expected_delivery', 'Expected Delivery') },
            { id: 'directo_number', label: t('purchase_orders.index.columns.directo_number', 'Directo Nr') },
            { id: 'total', label: t('purchase_orders.index.columns.total', 'Total') },
            { id: 'created_by', label: t('purchase_orders.index.columns.created_by', 'Created By') },
        ],
        [t],
    );

    const quickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('purchase_orders.index.quick_filters.status', 'Status') },
            { id: 'supplier', label: t('purchase_orders.index.quick_filters.supplier', 'Supplier') },
            { id: 'project_lead', label: t('purchase_orders.index.quick_filters.project', 'Project') },
        ],
        [t],
    );

    const pagination = useMemo<PaginationMeta>(() => {
        const meta = purchaseOrders.meta;

        if (meta) {
            return meta;
        }

        return {
            current_page: purchaseOrders.current_page ?? 1,
            last_page: purchaseOrders.last_page ?? 1,
            per_page: purchaseOrders.per_page ?? 25,
            total: purchaseOrders.total ?? purchaseOrders.data.length,
            from: purchaseOrders.from ?? null,
            to: purchaseOrders.to ?? null,
        };
    }, [purchaseOrders]);

    const columnFilterOptions = useMemo<Record<string, Array<{ value: string; label: string }>>>(() => {
        const optionSets: Record<string, Map<string, string>> = {
            po_number: new Map(),
            supplier: new Map(),
            project_lead: new Map(),
            status: new Map(STATUS_OPTIONS.map((status) => [status, t(`purchase_orders.statuses.${status}`, status.replace(/_/g, ' '))])),
            order_date: new Map(),
            expected_delivery: new Map(),
            total: new Map(),
            created_by: new Map(),
        };

        suppliers.forEach((supplier) => optionSets.supplier.set(supplier.uuid, supplier.name));
        projects.forEach((project) => optionSets.project_lead.set(project.uuid, project.contract_name));
        leads.forEach((lead) => optionSets.project_lead.set(lead.uuid, lead.lead_number ?? lead.uuid));
        creators.forEach((creator) => optionSets.created_by.set(creator.uuid, creator.name));

        purchaseOrders.data.forEach((po) => {
            optionSets.po_number.set(po.po_number, po.po_number);

            if (po.supplier) {
                optionSets.supplier.set(po.supplier.uuid, po.supplier.name);
            }

            if (po.project) {
                optionSets.project_lead.set(po.project.uuid, po.project.contract_name);
            }

            if (po.lead?.uuid) {
                optionSets.project_lead.set(po.lead.uuid, po.lead.lead_number ?? po.lead.uuid);
            }

            if (po.order_date) {
                optionSets.order_date.set(po.order_date, formatDate(po.order_date));
            }

            if (po.expected_delivery_date) {
                optionSets.expected_delivery.set(po.expected_delivery_date, formatDate(po.expected_delivery_date));
            }

            optionSets.total.set(String(po.subtotal), formatCurrency(po.subtotal, po.currency));
        });

        return Object.fromEntries(
            Object.entries(optionSets).map(([column, options]) => [
                column,
                Array.from(options.entries()).map(([value, label]) => ({ value, label })),
            ]),
        );
    }, [creators, leads, projects, purchaseOrders.data, suppliers, t]);

    const filteredPurchaseOrders = useMemo(() => {
        let filtered = purchaseOrders.data.filter((po) => {
            return Object.entries(columnFilters).every(([column, values]) => {
                if (!values.length) {
                    return true;
                }

                if (column === 'supplier') {
                    return po.supplier ? values.includes(po.supplier.uuid) : false;
                }

                if (column === 'project_lead') {
                    return Boolean((po.project && values.includes(po.project.uuid)) || (po.lead?.uuid && values.includes(po.lead.uuid)));
                }

                if (column === 'order_date') {
                    return values.includes(po.order_date || '');
                }

                if (column === 'expected_delivery') {
                    return values.includes(po.expected_delivery_date || '');
                }

                if (column === 'total') {
                    return values.includes(String(po.subtotal));
                }

                // created_by is filtered server-side (values hold user uuids, not names).
                if (column === 'created_by') {
                    return true;
                }

                const value = purchaseOrderColumnValue(po, column);

                return typeof value === 'string' ? values.includes(value) : false;
            });
        });

        if (sortColumn && sortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = purchaseOrderColumnValue(a, sortColumn);
                const bValue = purchaseOrderColumnValue(b, sortColumn);

                if (aValue === null || aValue === undefined || aValue === '') {
                    return 1;
                }

                if (bValue === null || bValue === undefined || bValue === '') {
                    return -1;
                }

                let comparison = 0;

                if (aValue instanceof Date && bValue instanceof Date) {
                    comparison = aValue.getTime() - bValue.getTime();
                } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                    comparison = aValue - bValue;
                } else {
                    comparison = String(aValue).localeCompare(String(bValue));
                }

                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return filtered;
    }, [columnFilters, purchaseOrders.data, sortColumn, sortDirection]);

    const quickFilterNames = quickFilterDefinitions.filter((quick) => (columnFilters[quick.id] || []).length > 0).map((quick) => quick.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('purchase_orders.index.toolbar.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('purchase_orders.index.filters.all_purchase_orders', 'All Purchase Orders');

    useEffect(() => {
        const nextSearch = filters.search ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    useEffect(() => {
        if (!isMobile) {
            return;
        }

        setVisibleColumns(new Set(['po_number', 'supplier', 'status', 'expected_delivery', 'total']));
    }, [isMobile]);

    const handleFilterChange = (column: string, values: string[]) => {
        setColumnFilters((previous) => {
            const next = { ...previous };

            if (!values.length) {
                delete next[column];
            } else {
                next[column] = values;
            }

            return next;
        });

        if (column === 'status') {
            applyFilters({ status: values[0] || undefined, page: 1 });
        }

        if (column === 'supplier') {
            applyFilters({ supplier_id: values[0] || undefined, page: 1 });
        }

        if (column === 'project_lead') {
            const selectedValue = values[0];
            const isLead = leads.some((lead) => lead.uuid === selectedValue);

            applyFilters({
                project_id: selectedValue && !isLead ? selectedValue : undefined,
                lead_id: selectedValue && isLead ? selectedValue : undefined,
                page: 1,
            });
        }

        if (column === 'created_by') {
            applyFilters({ created_by: values[0] || undefined, page: 1 });
        }
    };

    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] || [];
        const exists = current.includes(value);
        const isServerFilter = ['status', 'supplier', 'project_lead', 'created_by'].includes(column);
        const nextValues = exists ? [] : isServerFilter ? [value] : [...current, value];
        handleFilterChange(column, nextValues);
    };

    const handleOrderDateFrom = (value: string) => {
        setOrderDateFrom(value);
        applyFilters({ date_from: value || undefined, page: 1 });
    };

    const handleOrderDateTo = (value: string) => {
        setOrderDateTo(value);
        applyFilters({ date_to: value || undefined, page: 1 });
    };

    const handleOverdueToggle = (checked: boolean) => {
        setOverdueOnly(checked);
        applyFilters({ overdue: checked ? true : undefined, page: 1 });
    };

    const resetOrderDates = () => {
        setOrderDateFrom('');
        setOrderDateTo('');
        applyFilters({ date_from: undefined, date_to: undefined, page: 1 });
    };

    const clearFilters = () => {
        setColumnFilters({});
        setSearchValue('');
        setOrderDateFrom('');
        setOrderDateTo('');
        setOverdueOnly(false);
        applyFilters({
            status: undefined,
            supplier_id: undefined,
            project_id: undefined,
            lead_id: undefined,
            created_by: undefined,
            date_from: undefined,
            date_to: undefined,
            overdue: undefined,
            search: undefined,
            page: 1,
        });
    };

    const handleSortChange = (column: string, direction: SortDirection) => {
        setSortColumn(column);
        setSortDirection(direction);
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((previous) => {
            const next = new Set(previous);
            next.delete(column);

            return next;
        });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((previous) => {
            const next = new Set(previous);
            next.add(column);

            return next;
        });
    };

    const visibleColumnDefinitions = columnDefinitions.filter((column) => visibleColumns.has(column.id));

    const isOverdue = (po: PurchaseOrderListItem) =>
        po.expected_delivery_date &&
        !['received', 'cancelled'].includes(po.status) &&
        new Date(po.expected_delivery_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

    const renderCell = (po: PurchaseOrderListItem, column: PurchaseOrderColumn) => {
        if (column === 'po_number') {
            return (
                <Link
                    href={route('purchase-orders.show', po.id)}
                    onClick={(event) => event.stopPropagation()}
                    className="hover:bg-accent text-foreground block w-full rounded px-2 py-1.5 text-left font-semibold transition-colors"
                >
                    {po.po_number}
                </Link>
            );
        }

        if (column === 'supplier') {
            return <span className="text-sm">{po.supplier?.name ?? '—'}</span>;
        }

        if (column === 'project_lead') {
            const convertedProject = po.project?.lead_id === po.lead?.id ? po.project : po.lead?.project;
            const hasConvertedLeadProject = Boolean(po.lead && convertedProject?.lead_id === po.lead.id);

            return (
                <div className="text-sm">
                    {hasConvertedLeadProject ? (
                        <>
                            <div className="font-medium">{convertedProject?.contract_name}</div>
                            <div className="text-muted-foreground text-xs">
                                {t('purchase_orders.index.lead_label', 'Lead')}: {po.lead?.lead_number}
                            </div>
                        </>
                    ) : po.project ? (
                        <>
                            <div className="font-medium">{po.project.contract_name}</div>
                            {po.project.contract_number ? <div className="text-muted-foreground text-xs">{po.project.contract_number}</div> : null}
                        </>
                    ) : po.lead ? (
                        <div className="text-muted-foreground text-xs">
                            {t('purchase_orders.index.lead_label', 'Lead')}: {po.lead.lead_number}
                        </div>
                    ) : (
                        <div className="font-medium">{t('purchase_orders.show.warehouse_stock', 'Warehouse / Stock')}</div>
                    )}
                </div>
            );
        }

        if (column === 'status') {
            return <StatusBadge status={po.status} />;
        }

        if (column === 'order_date') {
            return <span className="text-sm">{formatDate(po.order_date)}</span>;
        }

        if (column === 'expected_delivery') {
            const overdue = isOverdue(po);

            return (
                <div className={cn('text-sm', overdue && 'text-destructive')}>
                    {formatDate(po.expected_delivery_date)}
                    {overdue && (
                        <div className="flex items-center gap-1 text-xs font-medium">
                            <AlertCircle className="h-3 w-3" />
                            {t('purchase_orders.index.overdue', 'Overdue')}
                        </div>
                    )}
                </div>
            );
        }

        if (column === 'total') {
            return <span className="block text-right text-sm font-medium">{formatCurrency(po.subtotal, po.currency)}</span>;
        }

        if (column === 'directo_number') {
            return po.directo_number ? (
                <span className="bg-primary/10 text-primary inline-flex rounded-md px-2 py-0.5 text-xs font-semibold">{po.directo_number}</span>
            ) : (
                <span className="text-muted-foreground text-sm">—</span>
            );
        }

        if (column === 'created_by') {
            return <span className="text-muted-foreground text-sm">{creatorName(po)}</span>;
        }

        return null;
    };

    return (
        <IndexLayout
            breadcrumbs={breadcrumbs}
            title={t('purchase_orders.index.title', 'Purchase Orders')}
            subtitle={t('purchase_orders.index.subtitle', 'Create, track, and manage all purchase orders.')}
            actions={
                <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                        <Download className="mr-1 h-4 w-4" />
                        {t('purchase_orders.index.export', 'Export')}
                    </Button>
                    <Link href={route('purchase-orders.create')}>
                        <Button size="sm">
                            <Plus className="mr-1 h-4 w-4" />
                            {t('purchase_orders.index.new_po', 'New Purchase Order')}
                        </Button>
                    </Link>
                </div>
            }
        >
            <GenericToolbar
                quickFilterButtonLabel={quickFilterButtonLabel}
                quickFilterDefinitions={quickFilterDefinitions}
                columnDefinitions={columnDefinitions}
                columnFilterOptions={columnFilterOptions}
                columnFilters={columnFilters}
                columnFilterSearch={columnFilterSearch}
                setColumnFilterSearch={setColumnFilterSearch}
                toggleSingleFilter={toggleSingleFilter}
                handleFilterChange={handleFilterChange}
                clearFilters={clearFilters}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                isSortOpen={isSortOpen}
                setIsSortOpen={setIsSortOpen}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                clearSort={clearSort}
                search={searchValue}
                setSearch={setSearchValue}
                perPage={pagination.per_page}
                onPerPageChange={handlePerPageChange}
                viewMode="table"
                setViewMode={() => {}}
                showViewToggle={false}
                visibleColumns={visibleColumns}
                handleColumnShow={handleColumnShow}
                handleColumnHide={handleColumnHide}
                filteredCount={filteredPurchaseOrders.length}
                resourceName="purchase_orders"
                resourceNamePlural={t('purchase_orders.index.resource_plural', 'purchase orders')}
                sortableColumns={columnDefinitions}
                excludeFromMoreFilters={['status', 'supplier', 'project_lead']}
                customFilters={
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm font-medium">
                            <span>{t('purchase_orders.index.filters.order_date_range', 'Order date')}</span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                disabled={!orderDateFrom && !orderDateTo}
                                onClick={resetOrderDates}
                            >
                                {t('common.reset', 'Reset')}
                            </Button>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                            <div className="grid gap-1">
                                <label className="text-muted-foreground text-xs">{t('purchase_orders.index.filters.from_date', 'From date')}</label>
                                <DatePicker
                                    value={orderDateFrom}
                                    onValueChange={handleOrderDateFrom}
                                    placeholder={t('common.pick_date', 'Pick a date')}
                                />
                            </div>
                            <div className="grid gap-1">
                                <label className="text-muted-foreground text-xs">{t('purchase_orders.index.filters.to_date', 'To date')}</label>
                                <DatePicker
                                    value={orderDateTo}
                                    onValueChange={handleOrderDateTo}
                                    placeholder={t('common.pick_date', 'Pick a date')}
                                />
                            </div>
                        </div>
                        <label
                            htmlFor="po-overdue"
                            className="border-input bg-background flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2"
                        >
                            <Checkbox id="po-overdue" checked={overdueOnly} onCheckedChange={(checked) => handleOverdueToggle(checked === true)} />
                            <Label htmlFor="po-overdue" className="cursor-pointer text-sm font-normal">
                                {t('purchase_orders.index.filters.overdue_only', 'Overdue delivery only')}
                            </Label>
                        </label>
                    </div>
                }
            />

            <div className="space-y-4">
                <div className="bg-card mb-4 items-center justify-between gap-3 overflow-x-auto rounded-xl border px-4 py-2.5">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {visibleColumnDefinitions.map((column) => {
                                    const selectedFilters = columnFilters[column.id] || [];

                                    return (
                                        <TableHead key={column.id} className={cn(column.id === 'total' && 'text-right')}>
                                            <TableColumnHeader
                                                label={column.label}
                                                sortDirection={sortColumn === column.id ? sortDirection : null}
                                                onSortAscending={() => handleSortChange(column.id, 'asc')}
                                                onSortDescending={() => handleSortChange(column.id, 'desc')}
                                                onHide={() => handleColumnHide(column.id)}
                                                filterOptions={columnFilterOptions[column.id] || []}
                                                selectedFilters={selectedFilters}
                                                onFilterChange={(values) => handleFilterChange(column.id, values)}
                                                isFiltered={selectedFilters.length > 0}
                                            />
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredPurchaseOrders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={visibleColumnDefinitions.length || 1} className="text-muted-foreground py-16 text-center">
                                        <ClipboardList className="mx-auto mb-3 h-10 w-10 opacity-30" />
                                        {t('purchase_orders.index.no_results', 'No purchase orders found.')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredPurchaseOrders.map((po) => (
                                    <TableRow
                                        key={po.id}
                                        className={cn(
                                            'cursor-pointer transition-all',
                                            isOverdue(po)
                                                ? 'border-l-destructive/60 bg-destructive/5 hover:bg-destructive/10 hover:border-l-destructive/80 border-l-4'
                                                : 'hover:bg-muted/50',
                                        )}
                                        onClick={() => router.visit(route('purchase-orders.show', po.id))}
                                    >
                                        {visibleColumnDefinitions.map((column) => (
                                            <TableCell key={column.id} className={cn(column.id === 'total' && 'text-right')}>
                                                {renderCell(po, column.id)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                loading={loading}
                className="mt-6"
            />
        </IndexLayout>
    );
}
